import { generateId } from '@freelancer/datastore/testing/helpers';
import type { RichTextElement } from '@freelancer/html-renderer';
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { HtmlElementType } from '@freelancer/html-renderer/html-elements.model';
import { ArticleStateApi, ArticleTypeApi } from 'api-typings/users/users';
import type { SeoFaq } from '../hire-page-details';
import type { Article, ArticleLink } from './articles.model';

const RELATED_ARTICLE_COUNT = 5;
const RECOMMENDED_ARTICLE_COUNT = 4;

export interface GenerateArticleOptions {
  readonly id?: number;
  readonly userId: number;
  readonly title?: string;
  readonly description?: string;
  readonly content?: string;
  readonly seoUrl?: string;
  readonly createTime?: number;
  readonly updateTime?: number;
  readonly state?: string;
  readonly type?: string;
  readonly mainCategory?: {
    readonly name: string;
    readonly seoName: string;
  };
  readonly language?: string;
  readonly globalTagCategoryId?: number;
  readonly metaTitle?: string;
  readonly parsedContent?: readonly RichTextElement[];
  readonly coverPhotoUrl?: string;
  readonly breadcrumbs?: readonly ArticleLink[];
  readonly relatedArticles?: readonly ArticleLink[];
  readonly recommendedArticles?: readonly ArticleLink[];
  readonly faqs?: readonly SeoFaq[];
  readonly isPlaceholderCoverPhoto?: boolean;
}

export function generateArticleObject({
  id = generateId(),
  userId,
  title = 'Test article',
  description = 'This is a test description',
  content = defaultContent,
  createTime = Date.now() - 5 * 60 * 1000,
  updateTime = Date.now(),
  state = ArticleStateApi.APPROVED,
  type = ArticleTypeApi.CONTENT_HUB,
  parsedContent = defaultParsedContent,
  coverPhotoUrl = defaultImageUrl,
  mainCategory = { name: 'Test Article', seoName: 'test-article' },
  language = 'en',
  globalTagCategoryId = 1,
  breadcrumbs = [],
  faqs = [],
  isPlaceholderCoverPhoto = false,
}: GenerateArticleOptions): Article {
  const relatedArticles = [...new Array(RELATED_ARTICLE_COUNT)].map(() =>
    generateArticleLinkObject(),
  );

  const recommendedArticles = [...new Array(RECOMMENDED_ARTICLE_COUNT)].map(
    () => generateArticleLinkObject(),
  );

  return {
    id,
    userId,
    title,
    description,
    content,
    seoUrl: `test-article-${id}`,
    createTime,
    updateTime,
    state,
    type,
    mainCategory,
    language,
    globalTagCategoryId,
    metaTitle: `Test article ${id}`,
    parsedContent,
    coverPhotoUrl,
    breadcrumbs,
    relatedArticles,
    recommendedArticles,
    faqs,
    isPlaceholderCoverPhoto,
  };
}

export function generateArticleLinkObject({
  id = generateId(),
  description = 'This is a test description',
  readTime = '1 min read',
  thumbnail = defaultImageUrl,
} = {}): ArticleLink {
  return {
    id,
    title: `Test article ${id}`,
    url: `/articles/test-article-${id}`,
    description,
    readTime,
    thumbnail,
  };
}

// Default values
const defaultImageUrl = `/article/writing.jpg`;

const defaultContent =
  "<h2>If your business doesn't have a blog, you're missing out on a powerful marketing tool</h2>\r\n<p>If your business has a website (and, let's be honest, it needs one), a blog can be a powerful way to boost your revenue.</p>\r\n<p>Writing a blog may seem like a frivolous pastime with all the other concerns you have as a business owner, but blogging is actually one of the most effective marketing strategies you can employ. Not only will a blog drive traffic to your website and increase your leads, it will help you define your brand in the minds of your customers. It's an indispensable platform to market your business.</p>\r\n<h3>1. A blog positions you as an authority</h3>\r\n<p>A good blog helps build your profile as an expert in your industry. A business blog isn't a place to hard-sell your product or service. It's a platform to talk about the big issues in the industry you work in.</p>\r\n<p>By taking a stand and offering your opinion and insight on your industry, you show potential customers that you're knowledgeable and authoritative. This builds trust in your brand. It makes you appear more credible.</p>\r\n<p>A good way to build this kind of authority is to think of some of the most common questions your clients ask. You can address these in your blog to offer value to your audience, and show your knowledge and expertise.</p>\r\n<h3>2. A blog increases your visibility</h3>\r\n<p>Blogging puts your brand in front of an audience you might not otherwise have the chance to engage with. There's plenty of research to back up the effectiveness of business blogging.</p>\r\n<p>A study by Hubspot found that businesses that publish 16 or more blog posts per month get 3.5 times the amount of leads of businesses that publish 0&ndash;4 posts per month.</p>\r\n<p>Blogging helps you rank in search engines for terms that people are searching that relate to your business. These may be terms that don't specifically refer to buying your product or service, but rather to common questions people have about your industry. When you produce content that answers these questions (and that content is visible in search engines), it introduces you to a whole new audience.</p>\r\n<p>Search engines prioritize sites that frequently update their content. It's likely that your website's homepage will remain fairly static. But a regularly updated blog sends a signal to search engines that your site is active and that it's offering new content. Blogging regularly will help boost your ranking and market your brand.</p>\r\n";

const defaultParsedContent: readonly RichTextElement[] = [
  {
    type: HtmlElementType.H2,
    children: [
      {
        type: HtmlElementType.TEXT,
        value:
          "If your business doesn't have a blog, you're missing out on a powerful marketing tool",
      },
    ],
  },
  {
    type: HtmlElementType.PARAGRAPH,
    children: [
      {
        type: HtmlElementType.TEXT,
        value:
          "If your business has a website (and, let's be honest, it needs one), a blog can be a powerful way to boost your revenue.",
      },
    ],
  },
  {
    type: HtmlElementType.PARAGRAPH,
    children: [
      {
        type: HtmlElementType.TEXT,
        value:
          "Writing a blog may seem like a frivolous pastime with all the other concerns you have as a business owner, but blogging is actually one of the most effective marketing strategies you can employ. Not only will a blog drive traffic to your website and increase your leads, it will help you define your brand in the minds of your customers. It's an indispensable platform to market your business.",
      },
    ],
  },
  {
    type: HtmlElementType.H3,
    children: [
      {
        type: HtmlElementType.TEXT,
        value: '1. A blog positions you as an authority',
      },
    ],
  },
  {
    type: HtmlElementType.PARAGRAPH,
    children: [
      {
        type: HtmlElementType.TEXT,
        value:
          "A good blog helps build your profile as an expert in your industry. A business blog isn't a place to hard-sell your product or service. It's a platform to talk about the big issues in the industry you work in.",
      },
    ],
  },
  {
    type: HtmlElementType.PARAGRAPH,
    children: [
      {
        type: HtmlElementType.TEXT,
        value:
          "By taking a stand and offering your opinion and insight on your industry, you show potential customers that you're knowledgeable and authoritative. This builds trust in your brand. It makes you appear more credible.",
      },
    ],
  },
  {
    type: HtmlElementType.PARAGRAPH,
    children: [
      {
        type: HtmlElementType.TEXT,
        value:
          'A good way to build this kind of authority is to think of some of the most common questions your clients ask. You can address these in your blog to offer value to your audience, and show your knowledge and expertise.',
      },
    ],
  },
  {
    type: HtmlElementType.H3,
    children: [
      {
        type: HtmlElementType.TEXT,
        value: '2. A blog increases your visibility',
      },
    ],
  },
  {
    type: HtmlElementType.PARAGRAPH,
    children: [
      {
        type: HtmlElementType.TEXT,
        value:
          "Blogging puts your brand in front of an audience you might not otherwise have the chance to engage with. There's plenty of research to back up the effectiveness of business blogging.",
      },
    ],
  },
  {
    type: HtmlElementType.PARAGRAPH,
    children: [
      {
        type: HtmlElementType.TEXT,
        value:
          'A study by Hubspot found that businesses that publish 16 or more blog posts per month get 3.5 times the amount of leads of businesses that publish 0\u20134 posts per month.',
      },
    ],
  },
  {
    type: HtmlElementType.PARAGRAPH,
    children: [
      {
        type: HtmlElementType.TEXT,
        value:
          "Blogging helps you rank in search engines for terms that people are searching that relate to your business. These may be terms that don't specifically refer to buying your product or service, but rather to common questions people have about your industry. When you produce content that answers these questions (and that content is visible in search engines), it introduces you to a whole new audience.",
      },
    ],
  },
  {
    type: HtmlElementType.PARAGRAPH,
    children: [
      {
        type: HtmlElementType.TEXT,
        value:
          "Search engines prioritize sites that frequently update their content. It's likely that your website's homepage will remain fairly static. But a regularly updated blog sends a signal to search engines that your site is active and that it's offering new content. Blogging regularly will help boost your ranking and market your brand.",
      },
    ],
  },
];
